import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../components/ui';
import {
  CostSummary, UserCostRow, EndpointCostRow, RateRow,
  fetchSummary, fetchByUser, fetchByEndpoint, fetchRates, updateRate, exportUrl,
} from '../lib/aiCostApi';
import './AiCost.css';
import { useTranslation } from 'react-i18next';

interface AiCostProps { apiUrl: string; }

// ── formatters: tokens are the primary unit, USD is a secondary estimate ──
const fmtTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};
const usd = (n: number) => `$${n.toFixed(2)}`;
const estUsd = (n: number) => `~${usd(n)} est`;

/** Last 6 YYYY-MM periods, newest first. */
function recentPeriods(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(new Date(d.getFullYear(), d.getMonth() - i, 1).toISOString().slice(0, 7));
  }
  return out;
}

const SOURCE_BADGE: Record<string, { labelKey: string; cls: string }> = {
  corporate: { labelKey: 'pages.aiCost.sourceCorporate', cls: 'badge-success' },
  local: { labelKey: 'pages.aiCost.sourceLocal', cls: 'badge-info' },
  endpoint: { labelKey: 'pages.aiCost.sourceEndpoint', cls: 'badge-unknown' },
  agent: { labelKey: 'pages.aiCost.sourceAgent', cls: 'badge-info' },
};

const AiCost: React.FC<AiCostProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(recentPeriods()[0]);
  const [tab, setTab] = useState<'user' | 'endpoint'>('user');
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [users, setUsers] = useState<UserCostRow[]>([]);
  const [endpoints, setEndpoints] = useState<EndpointCostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Rates panel
  const [showRates, setShowRates] = useState(false);
  const [rates, setRates] = useState<RateRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, u, e] = await Promise.all([
        fetchSummary(apiUrl, period),
        fetchByUser(apiUrl, period),
        fetchByEndpoint(apiUrl),
      ]);
      setSummary(s); setUsers(u); setEndpoints(e);
    } catch (err: any) {
      setError(err.message || 'Failed to load cost data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, period]);

  useEffect(() => { load(); }, [load]);

  const loadRates = async () => {
    try { setRates(await fetchRates(apiUrl)); } catch (e: any) { setError(e.message); }
  };
  const openRates = () => { setShowRates(true); loadRates(); };

  const saveRate = async (pattern: string, inMtok: number, outMtok: number) => {
    try {
      await updateRate(apiUrl, pattern, inMtok, outMtok);
      await loadRates();
      await load(); // re-price shadow estimates with the new rate
    } catch (e: any) { setError(e.message); }
  };

  // CSV export must go through fetch (auth header) → blob download.
  const download = async (group: 'user' | 'endpoint' | 'model') => {
    try {
      const res = await fetch(exportUrl(apiUrl, group, period));
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `ai-cost-${group}-${period}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) { setError(e.message); }
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="page-content aicost">
      <div className="page-title">
        <div>
          <h1><Icon name="bolt" size={20} /> {t('pages.aiCost.aiCost')}</h1>
          <p className="subtitle">
            {t('pages.aiCost.costOfAIAttributedToUsers')} <strong>{t('pages.aiCost.usdIsAnEstimate')}</strong> {t('pages.aiCost.fromListPriceRates')}
          </p>
        </div>
        <div className="aicost-actions">
          <select className="ent-input" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label={t('pages.aiCost.billingPeriod')}>
            {recentPeriods().map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button className="ent-btn" onClick={openRates}><Icon name="settings" /> {t('pages.aiCost.rates')}</button>
          <button className="ent-btn" onClick={() => download(tab)}><Icon name="download" /> {t('pages.aiCost.exportTokens')}</button>
        </div>
      </div>

      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}

      {/* Headline tiles — tokens first, USD as estimate */}
      {summary && (
        <div className="metric-row">
          <div className="metric accent-info">
            <span className="label"><Icon name="bolt" /> {t('pages.aiCost.totalTokens')}</span>
            <div className="value">{fmtTokens(summary.sanctioned_tokens + summary.shadow_tokens)}</div>
            <div className="aicost-tile-sub">{estUsd(summary.est_total_usd)}</div>
          </div>
          <div className="metric accent-success">
            <span className="label"><Icon name="checkBadge" /> {t('pages.aiCost.sanctioned')}</span>
            <div className="value">{fmtTokens(summary.sanctioned_tokens)}</div>
            <div className="aicost-tile-sub">{estUsd(summary.sanctioned_usd)}</div>
          </div>
          <div className="metric accent-warning">
            <span className="label"><Icon name="warning" /> {t('pages.aiCost.shadowAI')}</span>
            <div className="value">{fmtTokens(summary.shadow_tokens)}</div>
            <div className="aicost-tile-sub">{t('pages.aiCost.exposureFloor', { v0: estUsd(summary.shadow_usd) })}</div>
          </div>
          <div className="metric accent-info">
            <span className="label"><Icon name="users" /> {t('pages.aiCost.coverage')}</span>
            <div className="value">{summary.user_count}<span className="aicost-tile-unit"> {t('pages.aiCost.users')}</span></div>
            <div className="aicost-tile-sub">{t('pages.aiCost.endpoints', { v0: summary.endpoint_count })}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="ent-toolbar">
        <div className="seg">
          <button className={tab === 'user' ? 'active' : ''} onClick={() => setTab('user')}>{t('pages.aiCost.byUser')}</button>
          <button className={tab === 'endpoint' ? 'active' : ''} onClick={() => setTab('endpoint')}>{t('pages.aiCost.byEndpoint')}</button>
        </div>
        <button className="ent-btn spacer" onClick={load} disabled={loading}>
          <Icon name="refresh" className={loading ? 'spin' : ''} /> {t('pages.aiCost.refresh')}
        </button>
      </div>

      {loading ? (
        <p className="muted-text">{t('pages.aiCost.loadingCostData')}</p>
      ) : tab === 'user' ? (
        <UserTable rows={users} />
      ) : (
        <EndpointTable rows={endpoints} expanded={expanded} onToggle={toggle} />
      )}

      <p className="aicost-caveat">
        <Icon name="info" size={13} /> {t('pages.aiCost.shadowAITokensAreEstimatedFrom')} <strong>{t('pages.aiCost.exposureFloor2')}</strong>{t('pages.aiCost.notABillShadowFiguresAre')}
      </p>

      {showRates && (
        <RatesPanel rates={rates} onClose={() => setShowRates(false)} onSave={saveRate} />
      )}
    </div>
  );
};

const UserTable: React.FC<{ rows: UserCostRow[] }> = ({ rows }) => {
  const { t } = useTranslation();
  if (rows.length === 0) return <div className="ent-empty"><span className="icon"><Icon name="users" /></span><h4>{t('pages.aiCost.noAIUsageYet')}</h4><p>{t('pages.aiCost.sanctionedGatewayUsageAndAttributedShadow')}</p></div>;
  return (
    <div className="ent-table-wrap">
      <table className="ent-table">
        <thead>
          <tr>
            <th>{t('pages.aiCost.user')}</th><th>{t('pages.aiCost.source')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.tokens')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.sanctioned')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.shadow')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.estCost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const b = SOURCE_BADGE[r.source] || SOURCE_BADGE.endpoint;
            return (
              <tr key={r.user_key}>
                <td style={{ fontWeight: 500 }}>
                  {r.user_label}
                  {r.all_shadow && <span className="badge badge-warning aicost-flag" title={t('pages.aiCost.allUsageIsShadowAI')}><Icon name="warning" size={11} /> {t('pages.aiCost.allShadow')}</span>}
                </td>
                <td><span className={`badge ${b.cls}`}>{t(b.labelKey)}</span></td>
                <td className="aicost-num">{fmtTokens(r.tokens)}<span className="aicost-unit"> {t('pages.aiCost.tok')}</span></td>
                <td className="aicost-num aicost-muted">{fmtTokens(r.sanctioned_tokens)}</td>
                <td className="aicost-num aicost-muted">{fmtTokens(r.shadow_tokens)}</td>
                <td className="aicost-num aicost-est">{estUsd(r.est_total_usd)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const EndpointTable: React.FC<{ rows: EndpointCostRow[]; expanded: Set<string>; onToggle: (id: string) => void }> = ({ rows, expanded, onToggle }) => {
  const { t } = useTranslation();
  if (rows.length === 0) return <div className="ent-empty"><span className="icon"><Icon name="chip" /></span><h4>{t('pages.aiCost.noEndpointActivity')}</h4><p>{t('pages.aiCost.shadowAIUsageAttributedToEnrolled')}</p></div>;
  return (
    <div className="ent-table-wrap">
      <table className="ent-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>{t('pages.aiCost.endpoint')}</th><th>{t('pages.aiCost.os')}</th><th>{t('pages.aiCost.users2')}</th><th>{t('pages.aiCost.topAppsDests')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.tokens')}</th>
            <th style={{ textAlign: 'right' }}>{t('pages.aiCost.estCost')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = expanded.has(r.device_id);
            return (
              <React.Fragment key={r.device_id}>
                <tr className={r.shared ? 'aicost-shared-row' : ''}>
                  <td>
                    {r.shared && (
                      <button className="icon-btn aicost-expand" onClick={() => onToggle(r.device_id)} aria-label={t('pages.aiCost.togglePerUserBreakdown')}>
                        <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={14} />
                      </button>
                    )}
                  </td>
                  <td style={{ fontWeight: 500 }}>
                    {r.hostname || r.device_id}
                    {r.shared && <span className="badge badge-info aicost-flag" title={t('pages.aiCost.sharedMultiUserHostCostSplit')}><Icon name="users" size={11} /> {t('pages.aiCost.shared')}</span>}
                  </td>
                  <td>{r.os ? <span className="code-token muted">{r.os}</span> : <span className="aicost-muted">–</span>}</td>
                  <td>{r.users.length}</td>
                  <td className="aicost-apps">{r.top_apps.slice(0, 3).join(', ') || '–'}</td>
                  <td className="aicost-num">{fmtTokens(r.shadow_tokens)}<span className="aicost-unit"> {t('pages.aiCost.tok')}</span></td>
                  <td className="aicost-num aicost-est">{estUsd(r.est_usd)}</td>
                </tr>
                {r.shared && isOpen && r.per_user.map((s, i) => (
                  <tr key={`${r.device_id}-u${i}`} className="aicost-subrow">
                    <td></td>
                    <td colSpan={3} className="aicost-subuser">↳ {s.user_label}</td>
                    <td></td>
                    <td className="aicost-num aicost-muted">{fmtTokens(s.tokens)}</td>
                    <td className="aicost-num aicost-est">{estUsd(s.est_usd)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RatesPanel: React.FC<{ rates: RateRow[]; onClose: () => void; onSave: (pattern: string, inMtok: number, outMtok: number) => void }> = ({ rates, onClose, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, { in: string; out: string }>>({});
  const val = (p: RateRow, field: 'in' | 'out') =>
    draft[p.model_pattern]?.[field] ?? String(field === 'in' ? p.in_rate_mtok : p.out_rate_mtok);
  const set = (pattern: string, field: 'in' | 'out', v: string) =>
    setDraft((d) => ({ ...d, [pattern]: { in: d[pattern]?.in ?? '', out: d[pattern]?.out ?? '', [field]: v } as any }));

  return (
    <>
      <div className="ent-overlay" onClick={onClose} />
      <aside className="ent-drawer aicost-rates" role="dialog" aria-modal="true">
        <div className="ent-drawer-head">
          <h2>{t('pages.aiCost.modelPricing1MTokens')}</h2>
          <button className="icon-btn" onClick={onClose} aria-label={t('pages.aiCost.close')}><Icon name="close" /></button>
        </div>
        <div className="ent-drawer-body">
          <p className="muted-text" style={{ marginTop: 0 }}>
            {t('pages.aiCost.ratesAre')} <strong>{t('pages.aiCost.listPriceInUSDPer1')}</strong>{t('pages.aiCost.editingARateRePricesShadow')}
          </p>
          <table className="ent-table">
            <thead><tr><th>{t('pages.aiCost.modelPattern')}</th><th style={{ width: 110 }}>{t('pages.aiCost.input')}</th><th style={{ width: 110 }}>{t('pages.aiCost.output')}</th><th style={{ width: 70 }}></th></tr></thead>
            <tbody>
              {rates.map((p) => (
                <tr key={p.model_pattern}>
                  <td className="mono">{p.model_pattern}</td>
                  <td><input className="ent-input aicost-rate-input" type="number" min="0" step="0.01" value={val(p, 'in')} onChange={(e) => set(p.model_pattern, 'in', e.target.value)} /></td>
                  <td><input className="ent-input aicost-rate-input" type="number" min="0" step="0.01" value={val(p, 'out')} onChange={(e) => set(p.model_pattern, 'out', e.target.value)} /></td>
                  <td>
                    <button className="ent-btn sm primary" onClick={() => onSave(p.model_pattern, Number(val(p, 'in')), Number(val(p, 'out')))}>
                      {t('pages.aiCost.save')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </aside>
    </>
  );
};

export default AiCost;
